import React, { useState, useEffect } from "react"
import { Card, Button, Form, Select, Typography, Flex, Tag, Spin } from "antd"
import styles from "./index.module.css"
import { CircledClose } from "ui/Icon/DSIcon/CircledClose"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { CustomForm } from "ui/Form"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getGuideDigitalWaivers } from "store/digital-waiver"
import { useDispatch } from "react-redux"
import { updateTrip } from "store/trips"
import { useParams } from "react-router-dom"
import { getGuideTrip } from "store/trips"
import { DocumentsAddedModal } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/modal/DocumentsAdded"
import { toaster } from "components/common/Toast"
const { Title, Text } = Typography
const ListingDigitalWaiver = () => {
  const { id } = useParams()
  const [t] = useTranslation()
  const [form] = Form.useForm()
  const [digitalWaivers, setDigitalWaivers] = useState([])
  const [selectedWaivers, setSelectedWaivers] = useState([])
  const [errorMessage, setErrorMessage] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { items, loading } = useSelector((state) => state.digitalWaiver)
  const dropdownOptions = items.map((item) => ({
    label: item.name,
    value: item.id // Changed from item.id to item.digital_waiver_id
  }))
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getGuideTrip(id)).then((res) => {
      setDigitalWaivers(res.data.digital_waiver_experiences || [])
    })
  }, [dispatch, id])
  useEffect(() => {
    // Wait for dropdownOptions to be loaded and digitalWaivers to be available
    if (digitalWaivers.length > 0 && items.length > 0) {
      // Use digital_waiver_id directly
      const selectedWaivers = digitalWaivers.map((dw) => dw.digital_waiver_id)
      setSelectedWaivers(selectedWaivers)
      form.setFieldsValue({ waivers: selectedWaivers })
    }
  }, [digitalWaivers, items, form])
  useEffect(() => {
    dispatch(getGuideDigitalWaivers(undefined, undefined, true))
  }, [dispatch])
  // waiverOptions removed, now using dropdownOptions from Redux
  const onFinish = (callback) => {
    form
      .validateFields()
      .then(processFormSubmission(callback))
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo)
      })
  }
  const processFormSubmission = (callback) => {
    const values = form.getFieldsValue()
    const selectedWaiverIds = values.waivers || []
    if (selectedWaiverIds.length > 5) {
      setErrorMessage("You can select up to 5 waivers only.")
      return // Do not trigger API if more than 5
    } else {
      setErrorMessage("")
    }
    const formData = new FormData()

    // Create a map of existing waivers for quick lookup
    const existingWaiversMap = {}
    digitalWaivers.forEach((waiver) => {
      existingWaiversMap[waiver.digital_waiver_id] = waiver.id
    })

    // Track the index for form data keys
    let formIndex = 0

    // Process selected waivers
    selectedWaiverIds.forEach((digitalWaiverId) => {
      // Check if this is an existing waiver (has an ID in our digitalWaivers)
      if (existingWaiversMap[digitalWaiverId]) {
        // For existing waivers, send only the ID
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, existingWaiversMap[digitalWaiverId])
      } else {
        // For new waivers, send only the digital_waiver_id
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][digital_waiver_id]`, digitalWaiverId)
      }
      formIndex++
    })

    // Handle removed waivers (only include _destroy for removed items)
    digitalWaivers.forEach((waiver) => {
      if (!selectedWaiverIds.includes(waiver.digital_waiver_id)) {
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, waiver.id)
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][_destroy]`, "true")
        formIndex++
      }
    })

    // Add step parameter
    formData.append("trip[step]", "digital_waivers")

    console.log("FormData entries:")
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": " + pair[1])
    }

    dispatch(updateTrip(id, formData))
    if (callback) callback()
  }

  const handleValuesChange = (changedValues, allValues) => {}
  const handleAddDigitalWaiver = () => {
    // Logic to add a new digital waiver (e.g., open a modal)
    setIsModalOpen(true)
  }

  return (
    <Spin spinning={loading}>
      <div className="listing-digital-waiver">
        <CustomForm
          initialValues={{ waivers: selectedWaivers }}
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={handleValuesChange}
        >
          <Card>
            <Flex justify="space-between">
              <Title level={3} style={{ marginBottom: 24 }}>
                {t("listings.tabs.digital_waiver.digital_waiver_information")}
              </Title>
              <Button color="primary" variant="solid" onClick={handleAddDigitalWaiver}>
                {t("listings.tabs.digital_waiver.add_digital_waiver")}
              </Button>
            </Flex>

            <Form.Item name="waivers">
              <Flex>
                <Text>{t("listings.tabs.digital_waiver.add_digital_waiver_document")}</Text>
              </Flex>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select waiver documents"
                optionLabelProp="label"
                options={dropdownOptions}
                value={selectedWaivers}
                // value={form.getFieldValue('waivers')}
                tagRender={({ label, value, closable }) => {
                  const handleClose = (e) => {
                    setErrorMessage("")
                    e.preventDefault()
                    const currentWaivers = form.getFieldValue("waivers") || []
                    const newWaivers = currentWaivers.filter((item) => item !== value)
                    form.setFieldsValue({ waivers: newWaivers })
                    setSelectedWaivers(newWaivers)
                  }

                  return (
                    <Tag
                      className={styles.categoryChip}
                      key={value}
                      closable={closable}
                      onClose={handleClose}
                      closeIcon={<CircledClose width={16} height={16} fill="var(--color-primary)" />}
                    >
                      {label}
                    </Tag>
                  )
                }}
                onChange={(waivers) => {
                  if (waivers.length > 5) {
                    toaster.error("You can attach a maximum of 5 waivers.")
                    return
                  }
                  form.setFieldsValue({ waivers })
                  setSelectedWaivers(waivers)
                }}
              />
            </Form.Item>
            {errorMessage && <Text type="danger">{errorMessage}</Text>}
          </Card>
          <ListingActionButtons form={form} onFinish={onFinish} />
        </CustomForm>
        {isModalOpen && (
          <DocumentsAddedModal
            isLoading={false}
            visible={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onSave={() => {
              setIsModalOpen(false)
              onFinish()
            }}
          />
        )}
      </div>
    </Spin>
  )
}

export default ListingDigitalWaiver
